import { useState } from 'react'
import { X, Plus, Loader2 } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import { parseFlexibleInput } from '@/utils'

interface InterestManagementProps {
  title: string
  interests: string[]
  onAdd: (newInterests: string[]) => Promise<void>
  onRemove: (interestToRemove: string) => Promise<void>
  placeholder?: string
  loading?: boolean
}

export const InterestManagement = ({
  title,
  interests,
  onAdd,
  onRemove,
  placeholder = "Add new interest...",
  loading = false
}: InterestManagementProps) => {
  const [newInterest, setNewInterest] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [isRemoving, setIsRemoving] = useState<string | null>(null)

  const handleAdd = async () => {
    if (!newInterest.trim()) return

    const parsedInterests = parseFlexibleInput(newInterest)
    if (parsedInterests.length === 0) return

    // Check for duplicates
    const duplicates = parsedInterests.filter(interest => 
      interests.some(existing => existing.toLowerCase() === interest.toLowerCase())
    )

    if (duplicates.length > 0) {
      alert(`Already have: ${duplicates.join(', ')}`)
      return
    }

    try {
      setIsAdding(true)
      await onAdd(parsedInterests)
      setNewInterest('')
    } catch (error) {
      console.error('Failed to add interests:', error)
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemove = async (interest: string) => {
    try {
      setIsRemoving(interest)
      await onRemove(interest)
    } catch (error) {
      console.error('Failed to remove interest:', error)
    } finally {
      setIsRemoving(null)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-[var(--text)]">{title}</h3>
      
      {/* Current Interests */}
      {interests.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {interests.map((interest, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-1 bg-primary-500/10 text-primary-700 rounded-full border border-primary-500/20"
            >
              <span className="text-sm">{interest}</span>
              <button
                onClick={() => handleRemove(interest)}
                disabled={isRemoving === interest || loading}
                className="text-primary-600 hover:text-primary-800 disabled:opacity-50"
              >
                {isRemoving === interest ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <X className="w-3 h-3" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add New Interest */}
      <div className="flex gap-2">
        <Input
          type="text"
          value={newInterest}
          onChange={(e) => setNewInterest(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-1"
          disabled={loading || isAdding}
        />
        <Button
          onClick={handleAdd}
          disabled={!newInterest.trim() || loading || isAdding}
          isLoading={isAdding}
          size="sm"
          variant="secondary"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {interests.length === 0 && (
        <p className="text-sm text-[var(--text)] opacity-60">
          No interests added yet. Add your first interest above.
        </p>
      )}
    </div>
  )
}
